import Settings from './Interfaces/Settings';
import State from './Interfaces/State';
import SliderMode from '../Types/SliderMode';

import SliderBreakpoint from './SliderBreakpoint';
import SliderGesture from './SliderGesture';
import SliderObserver from './SliderObserver';
import SlideMode from "./Types/SlideMode";

/**
 * Slider
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2025-12-18 Autoplay improvements.
 * @package felixkiesewetter-js/slider
 * @version 1.15.3
 */
class Slider
{

    private autoplayIndicator ?:HTMLElement;
    private autoplayTimeout ?:number;
    private autoplayResumeHandler ?:Function;
    private breakpoints :SliderBreakpoint[] = [];
    private container ?:HTMLElement;
    private controls ?:{ previous ?:HTMLElement, next ?:HTMLElement };
    private currentBreakpoint :SliderBreakpoint = new SliderBreakpoint;
    private currentIndex :number = 0; // Normalized slider index
    private currentPosition :number = 0; // Position used to move the wrapper
    private destroyedExternally :boolean = false; // TODO: rather use a private state.destroyed ...
    private directionLock :{ previous :boolean, next :boolean } = { previous: false, next: false };
    private items :NodeList|null = null;
    private itemsPerView :number = 1;
    private navigation ?:HTMLElement;
    private navigationItems ?:NodeList;
    private node ?:HTMLElement;
    private resizeTimeout :null|number = null;
    private settings :Settings = {
        autoplay: false,
        autoplayInterval: 2500,
        autoplayIndicator: true,
        autoplayIndicatorSelector: '.slider__autoplay-indicator',
        autoplayStopOnInteraction: true,
        appendSliderToNode: false,
        autoHeight: true,
        breakpoints: [],
        controls: false,
        controlsSelector: '.slider__control',
        controlsPreviousModifier: 'previous',
        controlsNextModifier: 'next',
        controlsDisabledModifier: 'disabled',
        controlsInNavigation: false,
        debug: false,
        gestures: true,
        gesturesWithMouse: true,
        itemSelector: '.slider__item',
        itemsPerView: 1,
        itemsPerViewGrow: false,
        itemsCurrentModifier: 'current',
        itemsNextModifier: 'next',
        itemsPreviousModifier: 'previous',
        keyboard: true,
        mode: Slider.MODE_SLIDE as SliderMode,
        navigation: false,
        navigationSelector: '.slider__navigation',
        navigationItemSelector: '.slider__navigation__item',
        navigationItemCurrentModifier: 'current',
        navigationNumber: Slider.NAVIGATION_NUMBER_ITEM,
        onAutoplayChange: (slider :Slider, state :State) => {},
        onBreakpointChange: (slider :Slider, breakpoint :SliderBreakpoint) => {},
        onReady: (slider :Slider, ready :boolean) => {},
        onResize: (slider :Slider, breakpoint :SliderBreakpoint) => {},
        onSlide: (slider :Slider, index :number) => {},
        onStateChange: (slider :Slider, state :State, changedKey :string, changedValue :any) => {},
        playVideos: true,
        slideMode: 'strict',
        slideStep: 1,
        startWith: null,
        transitionDuration: 500,
        transitionEasing: 'ease-in-out',
        viewportSelector: '.slider__viewport',
        wrapperSelector: '.slider__items'
    };
    private state :State = {
        active: false,      // Is this the currently active slider on the website
        autoplay: false,    // Is autoplay currently running
        observed: false,    // Is this slider currently being observed using SliderObserver instance
        visible: false,     // Is this slider currently visible in the viewport
        initialized: [],    // Which components or features have already been initialized
        sliding: false      // Is this slider instance currently performing a slide
    };
    private viewport ?:HTMLElement;
    private wrapper ?:HTMLElement;

    /**
     * Constructor.
     */
    constructor(node :HTMLElement|Node, options :Settings)
    {
        if (!node) {
            this.settings.debug && console.error('[Slider]', `Slider expects first parameter to be a DOM element but variable node is of type ${typeof node}`);
            return;
        }
        this.init(node as HTMLElement, options);
        return;
    }

    // ##############
    // Initialization
    // ##############

    /**
     * Initialise slider.
     */
    init(node :HTMLElement, options :Settings) :Slider
    {
        this.node = node;
        // @ts-ignore;
        this.node._initialInnerHTML = node.innerHTML; // TODO: This will reset all attribute and markup changes from outside!
        // @ts-ignore;
        this.node._initialStyle = node.style;
        // @ts-ignore;
        this.node._initialClassName = node.className;
        this
            .mergeSettings(options)
            .initBreakpoints()
            .initObserver()
            .resize(0);
        window.addEventListener('resize', () => { this.resize() });
        return this;
    }

    /**
     * Initialise autoplay.
     */
    initAutoplay() :Slider
    {
        if (!this.settings.autoplay || !this.node) {
            return this;
        }

        // Use indicator
        if (this.settings.autoplayIndicator) {
            let autoplayIndicator = this.node?.querySelector(this.settings.autoplayIndicatorSelector) as HTMLElement;
            if (!autoplayIndicator) {
                autoplayIndicator = document.createElement('div') as HTMLElement;
                autoplayIndicator.classList.add(this.settings.autoplayIndicatorSelector.substring(1));
                autoplayIndicator.style.setProperty('--d', `${this.settings.autoplayInterval}ms`);
                if (this.container) this.container.appendChild(autoplayIndicator);
            }
            this.autoplayIndicator = autoplayIndicator;
        }

        // Debug
        this.debug('initAutoplay()');

        // Initial enabled state of autoplay
        this.setState('autoplay', this.settings.autoplay);

        // Autoplay state change on interaction
        if (!this.settings.autoplayStopOnInteraction) {
            return this;
        }
        // TODO: initAutoplay() is called twice on initialization => why??? console.warn('init autoplay')
        // TODO: this appends to many event handlers => bad for the performance
        this.container?.addEventListener('click', () :void => {
            if (this.getState('autoplay')) {
                this.debug('hold autoplay after user interaction');
                this.toggleAutoplay(false);
            }
        });

        // The autoplayResumeHandler ensures that autoplay is resumed after user has clicked outside the slider node,
        // it is only added as event listener once per Slider instance on initialization.
        if (!this.autoplayResumeHandler) {
            this.autoplayResumeHandler = (e :Event) :void => {
                if (!(e.target === this.node || this.node?.contains(e.target as HTMLElement))) {
                    this.debug('Resume autoplay after click outside');
                    this.toggleAutoplay(true);
                }
            }
            window.addEventListener('click', this.autoplayResumeHandler);
        }

        return this;
    }

    /**
     * Initialise slider breakpoints.
     */
    initBreakpoints() :Slider
    {
        if (
            !this.settings.breakpoints ||
            (Array === this.settings.breakpoints.constructor && !this.settings.breakpoints.length) ||
            (Object === this.settings.breakpoints.constructor && !Object.keys(this.settings.breakpoints).length)
        ) {
            this.breakpoints = [ new SliderBreakpoint ];
        }
        else {
            let maxWidth :number = Infinity;
            if (Array === this.settings.breakpoints.constructor) {
                // TODO: Also account for array containing previously initialized SliderBreakpoint objects.
                this.settings.breakpoints.sort((b1, b2) => {
                    if (b1 > b2) {
                        return -1;
                    }
                    return 1;
                });
                this.settings.breakpoints.forEach((minWidth :key) => {
                    this.breakpoints.push( new SliderBreakpoint(minWidth, maxWidth) );
                    maxWidth = minWidth - 1;
                });
            }
            else if (Object === this.settings.breakpoints.constructor) {
                const sortKeysByMinWidth = Object
                    .keys(this.settings.breakpoints)
                    .sort((m1, m2) => {
                        if (parseInt(m1) > parseInt(m2)) {
                            return -1;
                        }
                        return 1;
                    });
                sortKeysByMinWidth.forEach((minWidth :string) => {
                    const breakpointConfig :object = this.settings.breakpoints[minWidth];
                    this.breakpoints.push(new SliderBreakpoint(
                        parseInt(minWidth),
                        maxWidth,
                        breakpointConfig.options || null,
                        breakpointConfig.onEnter || null,
                        breakpointConfig.onLeave || null
                    ));
                    maxWidth = parseInt(minWidth) - 1;
                });
            }
        }
        this.debug('initBreakpoints()', this.breakpoints);
        return this;
    }

    /**
     * Initialise container.
     */
    initContainer() :Slider
    {
        let container :HTMLElement|undefined = this.node;
        if (this.settings.appendSliderToNode) {
            container = document.createElement('div') as HTMLElement;
            this.node?.appendChild(container);
        }
        container?.classList.add('slider');
        container?.classList.add(`slider--mode-${this.settings.mode}`);
        if (this.settings.debug) container?.classList.add('slider--debug');
        this.container = container;
        return this;
    }

    /**
     * Initialise controls.
     */
    initControls() :Slider
    {
        if (this.settings.controls) {
            let previous;
            let next;
            if ('object' === typeof this.settings.controls) {
                previous = this.settings.controls[0];
                next = this.settings.controls[1];
            }
            else if (this.settings.controls && this.items && this.items.length) {
                previous = this.node?.querySelector(`${this.settings.controlsSelector}--${this.settings.controlsPreviousModifier}`);
                if (!previous) {
                    previous = document.createElement('button');
                    previous.textContent = '<';
                    previous.classList.add(`${this.settings.controlsSelector.substring(1)}`);
                    previous.classList.add(`${this.settings.controlsSelector.substring(1)}--${this.settings.controlsPreviousModifier}`);
                    if (this.settings.controlsInNavigation && this.settings.navigation) {
                        const previousNavigationItem = document.createElement('li');
                        previousNavigationItem.className = 'slider__navigation__item slider__navigation__item--previous';
                        previousNavigationItem.appendChild(previous);
                        this.navigation?.insertBefore(previousNavigationItem, this.navigationItems[0]);
                    }
                    else {
                        this.container?.appendChild(previous);
                    }
                }
                next = this.node?.querySelector(`${this.settings.controlsSelector}--${this.settings.controlsNextModifier}`);
                if (!next) {
                    next = document.createElement('button');
                    next.textContent = '>';
                    next.classList.add(`${this.settings.controlsSelector.substring(1)}`);
                    next.classList.add(`${this.settings.controlsSelector.substring(1)}--${this.settings.controlsNextModifier}`);
                    if (this.settings.controlsInNavigation && this.settings.navigation) {
                        const nextNavigationItem = document.createElement('li');
                        nextNavigationItem.className = 'slider__navigation__item slider__navigation__item--next';
                        nextNavigationItem.appendChild(next);
                        this.navigation?.appendChild(nextNavigationItem);
                    }
                    else {
                        this.container?.appendChild(next);
                    }
                }
            }
            if (previous || next) {
                this.controls = {};
            }
            if (previous) {
                previous.addEventListener('click', () :void => {
                    this.slide(this.getpreviousIndex());
                });
                if (this.controls) this.controls.previous = previous as HTMLElement;
            }
            if (next) {
                next.addEventListener('click', () :void => {
                    this.slide(this.getNextIndex());
                });
                if (this.controls) this.controls.next = next as HTMLElement;
            }
        }
        return this;
    }

    /**
     * Initialise gestures.
     */
    initGestures() :Slider
    {
        if (!this.settings.gestures || !this.items || 1 >= this.items.length) {
            return this;
        }
        this.container?.classList.add('slider--implements-gestures')
        new SliderGesture(this.wrapper ?? this.viewport, {
            debug: this.settings.debug,
            mouse: this.settings.gesturesWithMouse,
            distanceThreshold: (1 / this.settings.itemsPerView),
            onStart: () => {
                this.toggleTransition(false)
                    .holdAutoplay();
            },
            onRelease: (params :{}) :void => {
                this.toggleTransition(true);
                setTimeout(() :void => {
                    this.resumeAutoplay();
                }, 250);
                if (params.fire) {
                    0 < params.direction ? this.slide(this.getNextIndex()) : this.slide(this.getpreviousIndex());
                }
            },
            onReset: () :void => {}
        });
        return this;
    }

    /**
     * Initialise items.
     */
    initItems() :NodeList|null
    {
        const items = this.node?.querySelectorAll(this.settings.itemSelector);
        if (!items || !items.length) {
            this.settings.debug && console.error('[Slider]', `Slider needs at least one child with selector "${this.settings.itemSelector}"`);
            return null;
        }
        else {
            items.forEach(item => {
                item.classList.add('slider__item');
            });
        }
        if (this.node) {
            this.node.dataset.sliderItems = items.length.toString();
            this.node?.style.setProperty('--slider-items', items.length.toString());
        }
        return items;
    }

    /**
     * Init items per view.
     */
    initItemsPerView() :Slider
    {
        // Compatibility fallback.
        if (Slider.ITEMS_PER_VIEW_AUTO === this.settings.itemsPerView) {
            this.settings.itemsPerView = Math.min(5, this.items.length);
            this.itemsPerView = parseInt(this.settings.itemsPerView);
        }
        else this.itemsPerView = this.settings.itemsPerView;

        // Debug and return current items per view.
        this.debug('initItemsPerView()', this.itemsPerView);
        this.node?.style.setProperty('--slider-items-per-view', this.itemsPerView.toString());
        return this;
    }

    /**
     * Initialise keyboard controls.
     */
    initKeyboardControls() :Slider
    {
        if (!this.settings.keyboard || 0 <= this.state.initialized.indexOf('keyboard')) {
            return this;
        }
        window.addEventListener('keyup', (e) => {
            if (!this.isVisible) {
                return this;
            }
            if (37 <= e.keyCode && 40 >= e.keyCode) {
                e.preventDefault();
            }
            if (39 === e.keyCode || 40 === e.keyCode) {
                this.slide(this.getNextIndex());
            }
            else if (37 === e.keyCode || 38 === e.keyCode) {
                this.slide(this.getpreviousIndex());
            }
        });
        this.state.initialized.push('keyboard');
        return this;
    }

    /**
     * Initialise navigation.
     */
    initNavigation() :Slider
    {
        if (this.settings.navigation) {
            let navigation = this.node?.querySelector(this.settings.navigationSelector);
            if (!navigation) {
                navigation = document.createElement('ul');
                navigation.classList.add(this.settings.navigationSelector.substring(1));
                let index = 1;
                this.items?.forEach(() => {
                    let navigationItem = document.createElement('li');
                    navigationItem.innerHTML = `<span>${index}</span>`;
                    navigation?.appendChild(navigationItem);
                    index ++;
                });
                this.container?.appendChild(navigation);
            }
            this.navigation = navigation as HTMLElement;
            this.initNavigationItems();
        }
        return this;
    }

    /**
     * Get navigation items
     *
     * @returns {Slider}
     */
    initNavigationItems()
    {
        if (!this.navigation) {
            return this;
        }
        const navigationItems = this.navigation.querySelectorAll('li');
        Object.keys(navigationItems).forEach((index) => {
            navigationItems[index].classList.add('slider__navigation__item');
            navigationItems[index].addEventListener('click', () => {
                const currentIteration = Math.floor(this.currentPosition / this.items.length);
                const slideTo = (currentIteration * this.items.length) + parseInt(index);
                this.slide(slideTo);
            });
        });
        this.navigationItems = navigationItems;
        return this;
    }

    /**
     * Initialize a global intersection observer for all sliders on a page
     */
    initObserver()
    {
        SliderObserver.init(this);
        return this;
    }

    /**
     * Initialise viewport
     *
     * @returns {Slider}
     */
    initViewport()
    {
        let viewport = this.node?.querySelector(this.settings.viewportSelector);
        if (!viewport) {
            viewport = document.createElement('div');
            viewport.classList.add(this.settings.viewportSelector.substring(1));
            this.container.appendChild(viewport);
        }
        if (Slider.MODE_SLIDE !== this.settings.mode) {
            for (const item of this.items) {
                viewport.appendChild(item);
            }
        }
        this.viewport = viewport;
        return this;
    }

    /**
     * Initialise wrapper
     *
     * @returns {Slider}
     */
    initWrapper()
    {
        let wrapper = this.node?.querySelector(this.settings.wrapperSelector);
        if (!wrapper && Slider.MODE_SLIDE === this.settings.mode) {
            wrapper = document.createElement('div');
            wrapper.classList.add(this.settings.wrapperSelector.substring(1));
        }
        for (const item of this.items) {
            wrapper.appendChild(item);
        }
        this.toggleTransition(true);
        this.viewport.appendChild(wrapper);
        this.wrapper = wrapper;
        return this;
    }

    // 
    // Getters
    // 

    /**
     * Public getter for a specific slider item which either matches given index or selector or contains given selector.
     */
    getItem(indexOrSelector :number|string) :Node|HTMLElement|null
    {
        const items :NodeList = this.initItems();
        if (typeof indexOrSelector === 'number') {
            return items[indexOrSelector] || null;
        }
        if ('' === indexOrSelector.trim()) {
            return null;
        }
        //@ts-ignore
        const matches :[] =  Array.from(items).filter((el :HTMLElement) => {
            return el.matches(indexOrSelector) || el.querySelector(indexOrSelector)
        });
        //@ts-ignore
        return matches[0] || null;
    }

    /**
     * Public getter for the index of a slider item which either matches or contains given selector.
     */
    getItemIndex(selector :string) :number|null
    {
        //@ts-ignore
        const index = Array.from(this.initItems()).indexOf(this.getItem(selector));
        return index >= 0 ? index : null;
    }

    /**
     * Public getter for all slider items
     *
     * @returns {NodeList}
     */
    getItems()
    {
        return this.items || new NodeList;
    }

    /**
     * Get next index
     *
     * @returns {int}
     */
    getNextIndex()
    {
        if (this.directionLock?.next) {
            return this.currentPosition;
        }
        return this.currentPosition + this.settings.slideStep;
    }

    /**
     * Public getter for the node
     *
     * @returns {null}
     */
    getNode()
    {
        return this.node;
    }

    /**
     * Get previous index
     *
     * @returns {int}
     */
    getpreviousIndex()
    {
        if (this.directionLock.previous) {
            return this.currentPosition;
        }
        return this.currentPosition - this.settings.slideStep;
    }

    /**
     * Get settings from data attributes.
     */
    getSettingsFromDataAttributes() :{}
    {
        let settingsFromDataAttributes :{} = {};
        if (this.node?.getAttribute('data-items-per-view')) {
            settingsFromDataAttributes.itemsPerView = parseInt(this.node.getAttribute('data-items-per-view'));
        }
        if (this.node?.getAttribute('data-navigation')) {
            settingsFromDataAttributes.navigation = '1' === this.node.getAttribute('data-navigation') ? true : false;
        }
        if (this.node?.getAttribute('data-controls')) {
            settingsFromDataAttributes.controls = '1' === this.node.getAttribute('data-controls') ? true : false;
        }
        return settingsFromDataAttributes;
    }

    /**
     * Get current slider state object or a specific value of the state.
     */
    getState(key :keyof State|null = null) :State|[]|null|boolean
    {
        if (!key) {
            return this.state;
        }
        return (key in this.state) ? this.state[key] : null;
    }

    /**
     * Get current slider active state.
     */
    isActive() :boolean
    {
        return true === this.getState('active');
    }

    /**
     * Get current slider observation state
     *
     * @returns {boolean}
     */
    isObserved()
    {
        return this.getState('observed');
    }

    /**
     * Get current slider visible state.
     */
    isVisible() :boolean|[]|null
    {
        return this.getState('visible');
    }

    // #######
    // Setters
    // #######

    /**
     * Set a slider state.
     */
    setState(key :keyof State, state :boolean|[]) :Slider
    {
        if (!(key in this.state) || state === this.state[key]) {
            return this;
        }
        // @ts-ignore
        this.state[key] = state;
        this.callback('onStateChange', this, this.state, key, state);
        return this;
    }

    // ##############
    // Public methods
    // ##############

    /**
     * Build the slider markup, prepare and/or reset some variables.
     */
    build(internally = false) :Slider
    {
        if (this.isActive()) {
            return this;
        }
        if (internally && this.destroyedExternally) {
            this.debug('build()', 'cancelled (destroyed externally)');
            return this;
        }
        if (!internally && this.destroyedExternally) {
            this.destroyedExternally = false;
        }
        this.items = this.initItems();
        if (!this.items || !this.items.length) {
            this.debug('build()', 'cancelled (no items)');
            return this;
        }
        this.debug('build()', 'begin ...');
        const startPosition = this.settings.startWith || this.getItemIndex(window.location.hash) || 0;
        this.currentPosition = 0;
        this
            .setState('active', true)
            .initContainer()
            .initViewport()
            .initWrapper()
            .initNavigation()
            .initControls()
            .initKeyboardControls()
            .initAutoplay()
            .initGestures()
            .initItemsPerView()
            .slide(startPosition, true)
            .observe()
            .callback('onReady', this, true);
        this.debug('build()', '... done');
        return this;
    }

    /**
     * Destroy.
     */
    destroy(internally :boolean = false) :Slider
    {
        if (!this.isActive()) {
            return this;
        }
        this.destroyedExternally = !internally;
        this.setState('active', false);
        this.unobserve();
        // TODO begin: if any other script modified any attributes (e.g., class names) of items => this is lost here => find other solution!
        if (this.node) {
            // @ts-ignore
            this.node.innerHTML = this.node._initialInnerHTML;
            // @ts-ignore
            this.node.className = this.node._initialClassName;
            // @ts-ignore
            this.node.style = this.node._initialStyle;
        }
         // TODO end.
        this.wrapper = undefined;
        this.debug('destroy()');
        return this;
    }

    /**
     * Hold autoplay (stop and do not resume automatically).
     */
    holdAutoplay() :Slider
    {
        this.toggleAutoplay(false, false);
        return this;
    }

    /**
     * Slide to the next slide.
     */
    next() :void
    {
        this.slide(this.getNextIndex());
    }

    /**
     * Play videos
     */
    playVideos(item)
    {
        if (this.settings.playVideos && this.isVisible && item) {
            const video = item.querySelector('video');
            if (video) {
                this.debug('playVideos()');
                video.muted = true;
                video.play();
            }
        }
    }

    /**
     * Slide to the previous slide.
     */
    previous() :void
    {
        this.slide(this.getpreviousIndex());
    }

    /**
     * Rebuild instance from scratch.
     */
    rebuild() :void
    {
        this.destroy().build();
    }

    /**
     * Redistribute (change the number of) children of each slider item
     */
    redistributeItemChildren(childrenSelector, childrenPerItem)
    {
        const currentChildrenPerItem = this.getItem(0).querySelectorAll(childrenSelector).length;
        if (!currentChildrenPerItem || currentChildrenPerItem === childrenPerItem) {
            return;
        }
        const children = this.getNode().querySelectorAll(childrenSelector);
        this
            .destroy()
            .getNode()
            .innerHTML = '';
        let newItem;
        children.forEach((child, index) => {
            if (0 === index % childrenPerItem) {
                newItem = document.createElement('div');
                newItem.className = 'slider__item';
                this.getNode().append(newItem);
            }
            newItem.append(child);
        });
        this.build();
    }

    /**
     * Reset autoplay.
     */
    clearAutoplayTimeout() :Slider
    {
        clearTimeout(this.autoplayTimeout);
        this.autoplayTimeout = undefined;
        return this;
    }

    /**
     * Resume autoplay.
     */
    resumeAutoplay() :Slider
    {
        this.toggleAutoplay(true);
        return this;
    }

    /**
     * Slide to target index.
     */
    slide(targetIndex :number = 0, ignoreLock :boolean = false)
    {
        // Return if a sliding is currently locked
        if (!ignoreLock) {
            if (this.state.sliding) {
                return this;
            }
            this.state.sliding = true;
        }

        // Clear autoplay timeout so that autoplay duration is persistent
        this.clearAutoplayTimeout();

        // Direction and normalized target index
        let limitedTargetIndex :number;
        const normalizedTargetIndex = this.normalizeIndex(targetIndex);
        const direction = targetIndex === this.currentPosition ? 0 : (targetIndex < this.currentPosition ? -1 : 1);

        // Get items
        const targetItem :Node|null = this.items ? this.items[normalizedTargetIndex] : null;
        const currentItem :Node|null = this.items ? this.items[this.currentIndex] : null;

        // Perform slide depending on mode
        switch (this.settings.mode) {

            // Slide
            case Slider.MODE_SLIDE:

                if (targetIndex === this.currentPosition) {
                    break;
                }

                switch (this.settings.slideMode) {

                    // Check if the slider goes out of bounds and pre- or append items accordingly
                    case Slider.SLIDE_MODE_ENDLESS:
                        this.items.forEach((item, itemIndex) => {
                            if (
                                targetIndex > this.items.length - this.itemsPerView && // out of bounds to the right
                                itemIndex < targetIndex - (this.items.length - this.itemsPerView) // item is required to be appended
                            ) {
                                item.classList.add(`slider__item--appended`);
                            }
                            else {
                                setTimeout(() => item.classList.remove('slider__item--appended'), this.settings.transitionDuration);
                            }
                            if (
                                targetIndex < 0 && // out of bounds to the left
                                normalizedTargetIndex <= itemIndex // item is required to prepended
                            ) {
                                item.classList.add(`slider__item--prepended`);
                            }
                            else {
                                setTimeout(() => item.classList.remove('slider__item--prepended'), this.settings.transitionDuration);
                            }
                        });
                        const reset = 0 === normalizedTargetIndex; // slider reached original start position => reset
                        if (reset) {
                            setTimeout(() => {
                                this.toggleTransition(false);
                                this.currentPosition = 0;
                                this.node?.style.setProperty('--slider-position', this.currentPosition.toString());
                                this.items.forEach(item => item.classList.remove('slider__item--appended', 'slider__item--prepended'));
                                setTimeout(() => this.toggleTransition(true), 10);
                            }, this.settings.transitionDuration + 10);
                        }
                        this.currentPosition = targetIndex;
                        this.node?.style.setProperty('--slider-position', this.currentPosition.toString());
                        break;

                    // Return to first or last slide (depending on direction) if the target index exceeds the slider's bounds
                    case Slider.SLIDE_MODE_ROTATE:
                        if (direction > 0) {
                            limitedTargetIndex = (this.items.length - this.itemsPerView) < normalizedTargetIndex ? 0 : normalizedTargetIndex;
                        }
                        else {
                            limitedTargetIndex = Math.min(this.items.length - this.itemsPerView, normalizedTargetIndex);
                        }
                        this.currentPosition = limitedTargetIndex;
                        this.node?.style.setProperty('--slider-position', this.currentPosition.toString());
                        break;

                    // Never slide further than the slider's bounds
                    case Slider.SLIDE_MODE_STRICT:
                        limitedTargetIndex = Math.max(0, Math.min(this.items.length - this.itemsPerView, targetIndex));
                        this.currentPosition = limitedTargetIndex;
                        this.node?.style.setProperty('--slider-position', this.currentPosition.toString());
                        break;
                }

                break;

            // Fade
            case Slider.MODE_FADE:

                if (targetIndex === this.currentPosition) {
                    break;
                }

                targetItem.style.opacity = 0;
                targetItem.style.zIndex = 3;
                setTimeout(() => {
                    targetItem.style.transitionDuration = `${this.settings.transitionDuration}ms`;
                    targetItem.style.opacity = 1;
                }, 10);
                setTimeout(() => {
                    targetItem.style.zIndex = 2;
                    targetItem.style.transitionDuration = '0ms';
                    currentItem.style.zIndex = 1;
                }, this.settings.transitionDuration);
        }

        // Auto-height feature
        if (this.settings.autoHeight && targetItem) {
            // TODO: sometimes targetItem is undefined because normalizedTargetIndex is above item count?!
            this.node?.style.setProperty('--slider-height', `${targetItem.scrollHeight}px`);
        }

        // Stop or play videos
        if (currentItem !== targetItem) {
            this.stopVideos(currentItem);
        }
        this.playVideos(targetItem);

        // Update components and variables
        this.currentIndex = normalizedTargetIndex;
        this.container.dataset.sliderIndex = this.currentIndex;
        this
            .updateDirectionLock()
            .updateNavigation()
            .updateControls()
            .updateItems();

        // Post slide actions
        setTimeout(() => {
            this.state.sliding = false;
            this.progressAutoplay();
        }, this.settings.transitionDuration);
        this.callback('onSlide', this, targetIndex);
        return this;
    }

    /**
     * Stop videos.
     */
    stopVideos(item :HTMLElement|null = null) :void
    {
        if (null !== item) {
            const video = item.querySelector('video');
            if (video) {
                this.debug('stopVideos()', item);
                video.pause();
            }
        }
        else {
            try {
                this.items?.forEach((item :HTMLElement) => {
                    const video = item.querySelector('video');
                    if (video) {
                        this.debug('stopVideos()');
                        video.pause();
                    }
                });
            }
            catch(e) {
                console.warn(e);
            }
        }
    }

    /**
     * Toggle autoplay.
     */
    toggleAutoplay(enabled :boolean, resume :boolean = true) :Slider
    {
        if (!this.settings.autoplay || enabled === this.getState('autoplay')) {
            return this;
        }

        // Set
        this.setState('autoplay', enabled);

        console.log('toggleAutoplay', this.getState())

        // Debug
        this.debug('toggleAutoplay()', enabled, resume);

        switch (enabled) {
            case true:
                this.autoplayIndicator?.classList.remove(`${this.settings.autoplayIndicatorSelector.substring(1)}--hold`);
                break;
            case false:
                this.autoplayIndicator?.classList.add(`${this.settings.autoplayIndicatorSelector.substring(1)}--hold`);
                if (undefined === this.autoplayTimeout) {
                    break;
                }
                this.clearAutoplayTimeout();
                break;
        }

        if (resume) {
            this.progressAutoplay();
        }

        // Dispatch autoplay event
        this.callback('onAutoplayChange', this, {
            stopped: !enabled,
            started: enabled,
            interval: this.settings.autoplayInterval
        });

        // Update data attribute
        this.node?.setAttribute('data-slider-autoplay', enabled ? '1' : '0');
        return this;
    }

    // ###############
    // Private methods
    // ###############

    /**
     * Run callback.
     */
    callback(name :keyof Settings = 'onSlide', ...parameters)
    {
        if (Function !== this.settings[name].constructor) {
            return;
        }
        this.settings[name](...parameters);
    }

    /**
     * Debug any parameters.
     */
    debug(...parameters :any[]) :Slider
    {
        this.settings.debug && console.log(
            '%c[Slider]',
            'background: 222; color: bada55; padding: 4px;',
            ...parameters
        );
        return this;
    }

    /**
     * Merge default settings with instance options.
     */
    mergeSettings(options :Settings, storeSettings :boolean = false) :Slider
    {
        if (options.debug && 'string' === typeof options.debug && this.node) {
            options.debug = this.node.id === options.debug.replace('', '');
        }
        if (!storeSettings) {
            const dataSettings = this.getSettingsFromDataAttributes();
            this.settings = this.validateSettings({
                ...this.settings, ...dataSettings, ...options,
            });
        }
        else {
            this.settings.__restore = { ...this.settings };
            this.settings = this.validateSettings({
                ...this.settings, ...options,
            });
        }
        this.debug('mergeSettings()', this.settings);
        return this;
    }

    /**
     * Normalize the slider index.
     */
    normalizeIndex(index :number) :number
    {
        if (!this.items) {
            return 0;
        }
        else if (index < 0) {
            return this.items.length + index;
        }
        else if (index >= this.items.length) {
            return this.items.length % index;
        }
        return index;
    }

    /**
     * Stop observing this slider instance.
     */
    observe() :Slider
    {
        SliderObserver.observe(this);
        return this;
    }

    /**
     * Progress autoplay
     */
    progressAutoplay() :void
    {
        // Never run multiple autoplay actions at once
        if (
            'number' === typeof this.autoplayTimeout ||
            !this.settings.autoplay ||
            false === this.getState('autoplay') ||
            !this.isVisible()
        ) {
            return;
        }

        // TODO: On initialization this is called twice => not neccessary! console.warn('CONTINUE :)');

        // Set autoplay timeout
        this.autoplayTimeout = setTimeout(() => {
            this.autoplayTimeout = undefined;
            this.debug('autoplay');
            this.slide(this.getNextIndex());
        }, this.settings.autoplayInterval);
    }

    /**
     * Resize handler.
     */
    resize(delay :number = 500) :void
    {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() :void => {
            this.updateBreakpoint();
            if (Function === this.settings.onResize.constructor) {
                this.callback('onResize', this, this.currentBreakpoint);
            }
            this.debug('resize()');
        }, delay);
    }

    /**
     * Restore settings
     */
    restoreSettings()
    {
        if (!this.settings.__restore) {
            return this;
        }
        this.settings = { ...this.settings.__restore };
        return this;
    }

    /**
     * Enable or disable transition of the slider wrapper.
     */
    toggleTransition(enabled :boolean = true) :Slider
    {
        if (enabled) {
            this.container?.style.setProperty('--slider-transition-duration', `${this.settings.transitionDuration}ms`);
            this.node?.style.setProperty('--slider-transition-easing', this.settings.transitionEasing);
            return this;
        }
        this.container?.style.setProperty('--slider-transition-duration', '0');
        this.node?.style.setProperty('--slider-transition-easing', 'linear');
        return this;
    }

    /**
     * Stop observing this slider instance.
     */
    unobserve() :Slider
    {
        SliderObserver.observe(this);
        return this;
    }

    /**
     * Detect and update the current breakpoint.
     */
    updateBreakpoint() :Slider
    {
        this.currentBreakpoint = null;
        this.breakpoints.forEach((breakpoint :SliderBreakpoint) => {
            if (!breakpoint.applies(window.innerWidth)) {
                if (breakpoint.isActive()) {
                    breakpoint
                        .callback('onLeave', this)
                        .setActive(false);
                }
            }
            if (breakpoint.applies(window.innerWidth)) {
                this.currentBreakpoint = breakpoint;
                if (breakpoint.isActive()) {
                    return this;
                }
                breakpoint.setActive(true);
                if (breakpoint.getOptions()) {
                    this
                        .mergeSettings(breakpoint.getOptions(), true)
                        .rebuild();
                }
                else {
                    this
                        .restoreSettings()
                        .rebuild();
                }
                breakpoint.callback('onEnter', this);
                this.callback('onBreakpointChange', this, breakpoint);
            }
        });
        if (!this.currentBreakpoint) {
            this.destroy();
        }
        this.debug('updateBreakpoint()', this.currentBreakpoint);
        return this;
    }

    /**
     * Update controls' class name and lock status.
     */
    updateControls() :Slider
    {
        if (!this.controls) {
            return this;
        }
        const disabledClassName = `${this.settings.controlsSelector.substring(1)}--${this.settings.controlsDisabledModifier}`;
        Object.values(this.controls).forEach(control => control.classList.remove(disabledClassName));
        if (this.directionLock.previous && this.controls.previous) {
            this.controls.previous?.classList.add(disabledClassName);
        }
        if (this.directionLock.next && this.controls.next) {
            this.controls.next?.classList.add(disabledClassName);
        }
        return this;
    }

    /**
     * Update direction lock
     *
     * Lock sliding to the next or previous item(s)
     *
     * @returns {Slider}
     */
    updateDirectionLock()
    {
        switch (this.settings.slideMode) {
            case Slider.SLIDE_MODE_STRICT:
                this.directionLock.previous = (0 === this.currentPosition);
                this.directionLock.next = this.currentPosition >= this.items.length - this.itemsPerView;
                break;
            default:
                this.directionLock.previous = this.directionLock.next = false;
                break;
        }
        return this;
    }

    /**
     * Update items
     *
     * Current item gets an additional class name
     *
     * @returns {Slider}
     */
    updateItems()
    {
        const currentClassName =
            `${'.' === this.settings.itemSelector.substring(0, 1) ?
                this.settings.itemSelector.substring(1) :
                'slider__item'}--${this.settings.itemsCurrentModifier}`;
        const previousClassName =
            `${'.' === this.settings.itemSelector.substring(0, 1) ?
                this.settings.itemSelector.substring(1) :
                'slider__item'}--${this.settings.itemsPreviousModifier}`;
        const nextClassName =
            `${'.' === this.settings.itemSelector.substring(0, 1) ?
                this.settings.itemSelector.substring(1) :
                'slider__item'}--${this.settings.itemsNextModifier}`;
        this.node?.querySelectorAll(this.settings.itemSelector).forEach((item, index) => {
            item.dataset.sliderIndex = index;
            if (index === this.currentIndex) {
                item.classList.add(currentClassName);
            }
            else {
                item.classList.remove(currentClassName);
            }
            if ((index === this.currentIndex - 1) || (this.settings.slideMode === Slider.SLIDE_MODE_ENDLESS && this.currentIndex === 0 && index === this.items.length - 1)) {
                item.classList.add(previousClassName);
            }
            else {
                item.classList.remove(previousClassName);
            }
            if ((index === this.currentIndex + 1) || (this.settings.slideMode === Slider.SLIDE_MODE_ENDLESS && this.currentIndex === this.items.length - 1 && index === 0)) {
                item.classList.add(nextClassName);
            }
            else {
                item.classList.remove(nextClassName);
            }
        });
        return this;
    }

    /**
     * Update navigation.
     */
    updateNavigation() :Slider
    {
        if (!this.navigationItems || !this.items) {
            return this;
        }
        if (this.itemsPerView >= this.items.length) {
            this.navigationItems?.forEach(navigationItem => {
                navigationItem.style.display = 'none'
            });
            return this;
        }
        const targetSection = Math.floor(this.currentPosition / this.itemsPerView)
        this.navigationItems.forEach((navigationItem, index) => {
            const displayNavigationItem = !this.settings.slideItemByItem ? 0 === index % this.itemsPerView : true
            const navigationItemsPointsToSection = Math.floor(index / this.itemsPerView)
            const currentClassName = `${this.settings.navigationItemSelector.substring(1)}--${this.settings.navigationItemCurrentModifier}`
            navigationItem.style.display = displayNavigationItem ? 'block' : 'none'
            if (Slider.NAVIGATION_NUMBER_SECTION === this.settings.navigationNumber) {
                navigationItem.textContent = (navigationItemsPointsToSection + 1)
            }
            if (
                this.normalizeIndex(this.currentPosition) === parseInt(index) ||
                targetSection === navigationItemsPointsToSection
            ) {
                navigationItem.classList.add(currentClassName)
                return;
            }
            navigationItem.classList.remove(currentClassName)
        });
        return this;
    }

    /**
     * Validate settings.
     */
    validateSettings(settings :Settings = {}) :Settings
    {
        if (Slider.MODE_FADE === settings.mode) {
            if (Slider.SLIDE_MODE_ROTATE !== settings.slideMode) {
                settings.slideMode = Slider.SLIDE_MODE_ROTATE as SlideMode;
                console.warn('[Slider]', 'Fade mode does not support any slideMode settings other than "rotate"');
            }
            if (1 !== settings.itemsPerView) {
                settings.itemsPerView = 1;
                console.warn('[Slider]', 'Fade mode does not support any itemsPerView settings other than "1"');
            }
        }
        return settings;
    }

    static NAVIGATION_NUMBER_ITEM :string = 'item';
    static NAVIGATION_NUMBER_SECTION :string = 'section';
    static ITEMS_PER_VIEW_AUTO :string = 'auto';
    static SLIDE_MODE_STRICT :string = 'strict';
    static SLIDE_MODE_ROTATE :string = 'rotate';
    static SLIDE_MODE_ENDLESS :string = 'endless';
    static MODE_SLIDE :string = 'slide';
    static MODE_FADE :string = 'fade';
    static MODE_DISSOLVE :string = 'fade';

}

export default Slider;